import React, { useMemo } from 'react'
import { PRESCRIPTION_LAYOUTS, PRESCRIPTION_PRINT_TYPES } from 'src/consumer/constants'
import { PMPBodySectionItems } from './styledComponent'
import { PRESCRIPTION_COLORS_MAP } from '../../../inputComponents/common/color-utils'
import ThreeColumnPrescriptionFooterPatientDetails from 'src/mentor/components/PrescriptionMakingPage/prescriptionTemplate/header/patientDetails/ThreeColumnPrescriptionFooterPatientDetails'
import DefaultPatientDetails from 'src/mentor/components/PrescriptionMakingPage/prescriptionTemplate/header/patientDetails/DefaultPatientDetails'
import { convertTemplateTextPropertiesToStyle } from 'src/mentor/components/PrescriptionMakingPage/prescriptionTemplate/utils/textPropertiesUtils'

export default function PatientDetails({
  containerRef,
  debug,
  printType,
  prescriptionData,
  debugHighlightStates,
  prescriptionViewSettings,
}) {
  return (
    <PMPBodySectionItems
      ref={containerRef}
      noPadding
      debug={debug}
      debugColor={PRESCRIPTION_COLORS_MAP.patientDetails?.color}
      debugHighlight={debugHighlightStates?.patientDetails}
      data-prescription-patient-section="true"
    >
      <PmpPatientDetails
        prescriptionData={prescriptionData}
        leaveSpace={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
        prescriptionViewSettings={prescriptionViewSettings}
        debug={debug}
      />
    </PMPBodySectionItems>
  )
}

export function PmpPatientDetails({
  prescriptionData,
  leaveSpace,
  debug,
  prescriptionViewSettings,
}) {
  const patientInfoSettings = prescriptionData?.patientInfoSectionSettings

  // QR Code visibility
  let showQrCode = false
  if (patientInfoSettings?.showQRCode !== null && patientInfoSettings?.showQRCode !== undefined) {
    showQrCode = patientInfoSettings?.showQRCode
  } else {
    showQrCode = prescriptionViewSettings?.showPrescriptionQR || false
  }

  // Build customizations object with visibility and styling
  const customizations = useMemo(() => {
    if (!patientInfoSettings) {
      // No template settings, use legacy behavior
      return {
        showAgeGender: true,
        showMobile: prescriptionViewSettings?.showPatientPhoneOnPrescription || false,
        showAddress: prescriptionViewSettings?.showPatientAddressOnPrescription || false,
        showEmail: false,
      }
    }

    // Template settings exist - use them with fallback to legacy settings
    return {
      // Patient Name - always shown
      patientNameKeyStyle: convertTemplateTextPropertiesToStyle(patientInfoSettings.patientNameKey),
      patientNameValStyle: convertTemplateTextPropertiesToStyle(patientInfoSettings.patientNameVal),

      // Age/Gender
      showAgeGender: patientInfoSettings.showPatientAgeGender ?? true,
      patientAgeGenderKeyStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientAgeGenderKey,
      ),
      patientAgeGenderValStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientAgeGenderVal,
      ),

      // Mobile
      showMobile:
        patientInfoSettings.showPatientMobile !== null &&
        patientInfoSettings.showPatientMobile !== undefined
          ? patientInfoSettings.showPatientMobile
          : prescriptionViewSettings?.showPatientPhoneOnPrescription || false,
      patientMobileKeyStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientMobileKey,
      ),
      patientMobileValStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientMobileVal,
      ),

      // Address
      showAddress:
        patientInfoSettings.showPatientAddress !== null &&
        patientInfoSettings.showPatientAddress !== undefined
          ? patientInfoSettings.showPatientAddress
          : prescriptionViewSettings?.showPatientAddressOnPrescription || false,
      patientAddressKeyStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientAddressKey,
      ),
      patientAddressValStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientAddressVal,
      ),

      // Email
      showEmail: patientInfoSettings.showPatientEmail || false,
      patientEmailKeyStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientEmailKey,
      ),
      patientEmailValStyle: convertTemplateTextPropertiesToStyle(
        patientInfoSettings.patientEmailVal,
      ),
    }
  }, [patientInfoSettings, prescriptionViewSettings])

  const PatientDetailsComponent = useMemo(() => {
    switch (prescriptionData?.layout) {
      case PRESCRIPTION_LAYOUTS.threeColumnFooter:
      case PRESCRIPTION_LAYOUTS.twoColumnFooter:
      case PRESCRIPTION_LAYOUTS.twoColumnWithDisclaimerFooter:
      case PRESCRIPTION_LAYOUTS.twoColumnWithDisclaimerFooterWithDegreesInSeperateLines:
      case PRESCRIPTION_LAYOUTS.largerFontLayout:
      case PRESCRIPTION_LAYOUTS.listPointersFooter:
      case PRESCRIPTION_LAYOUTS.boxHeaderWithTwoColumnFooter:
      case PRESCRIPTION_LAYOUTS.twoDoctorHeaderWithListPointers:
        return ThreeColumnPrescriptionFooterPatientDetails
      default:
        return DefaultPatientDetails
    }
  }, [prescriptionData?.layout])

  return (
    <PatientDetailsComponent
      prescriptionData={prescriptionData}
      leaveSpace={leaveSpace}
      debug={debug}
      prescriptionViewSettings={prescriptionViewSettings}
      hideQRCode={!showQrCode}
      customizations={customizations}
    />
  )
}
